
import { isMobile } from 'helpers';
import { sessionKeys } from './storageKeys';

const deviceReadyPromise = (callback, timeout = 15000) => new Promise((resolve, reject) => {
  const abort = setTimeout(() => {
    document.removeEventListener('deviceready', onDeviceReady);
    reject(new Error('"deviceready" timeout'));
  }, timeout);

  const onDeviceReady = () => {
    clearTimeout(abort);

    // Throwing here would escape into the listener, leaving the promise pending with its timeout already cleared.
    try {
      callback(resolve, reject);
    } catch (error) {
      reject(error);
    }
  };

  document.addEventListener('deviceready', onDeviceReady, { passive: true, once: true });
});

const silentDeviceReadyPromise = (...args) => deviceReadyPromise(...args).catch(err => {
  // If error is a NativeStorageError's ITEM_NOT_FOUND. Just ignore it.
  if (err.code && err.constructor?.ITEM_NOT_FOUND && err.code === err.constructor?.ITEM_NOT_FOUND) {
    return null;
  }
  console.error(err);
  return null;
});

// Without cordova.js "deviceready" never fires, so every native call would sit out its timeout.
const isNativeAvailable = () => isMobile && Boolean(window.cordova);

const storage = {
  local: {
    get: async key => localStorage.getItem(key),
    set: async (key, value) => localStorage.setItem(key, value),
    remove: async key => localStorage.removeItem(key),
  },
  native: {
    isAvailable: isNativeAvailable,
    get: key => silentDeviceReadyPromise((resolve, reject) => window.NativeStorage.getItem(key, resolve, reject)),
    set: (key, value) => silentDeviceReadyPromise((resolve, reject) => window.NativeStorage.setItem(key, value, resolve, reject)),
    remove: key => silentDeviceReadyPromise((resolve, reject) => window.NativeStorage.remove(key, resolve, reject)),
  },
  secure: {
    // TODO...
  },
};

const getValue = async (key, quick = false) => {
  const token = await storage.local.get(key);

  // Skips backup. Needed for a quick retrieval during unload event.
  // During unload event cordova/NativeStorage no longer functions.
  if (quick) {
    return token;
  }

  if (!token && storage.native.isAvailable()) {
    const backup = await storage.native.get(key);

    if (backup) {
      await storage.local.set(key, backup);
      return backup;
    }
  }

  return token;
};

const setValue = async (key, value) => {
  if (storage.native.isAvailable()) {
    await Promise.all([
      storage.local.set(key, value),
      storage.native.set(key, value), // Awaited so a force-close after setValue resolves can't drop the native backup
    ]);
    return;
  }

  return storage.local.set(key, value);
};

const removeValue = async (key) => {
  if (storage.native.isAvailable()) {
    return Promise.all([storage.local.remove(key), storage.native.remove(key)]); // Important to clear both!
  }

  return storage.local.remove(key);
};

const removeValues = keys => Promise.all(keys.map(removeValue));

const clearSession = () => removeValues(sessionKeys);

export default {
  setValue,
  getValue,
  removeValue,
  removeValues,
  clearSession,
};
